/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../registry/Manager';
import { page as SamplePage } from '../../help/sample_pages/EmptyPage';
import { checkForFixedChild } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { cssGridOverflow, paperVariant } from '../../registry/constants';
import { defaultValue } from './Paper';

const name = 'Paper';

const PaperManager: ComponentManagerFn<'Paper'> = (
  platform: Platform,
): ComponentManager<'Paper'> => {
  return {
    ...baseComponentManager<'Paper'>(platform, 'Paper'),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          ...getCustomChildItemTypes('Paper'),
          'CanvasGrid',
          'Card',
          'Chartjs',
          'CombinationChart',
          'DataGrid',
          'DataStoreReference',
          'FlexPanel',
          'Form',
          'HorizontalSplitPanel',
          'IoTImageMapper',
          'OCRComponent',
          'RowPanel',
          'SearchForm',
          'TabPanel',
          'VerticalSplitPanel',
          'WorkflowViewer',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'elevation':
          return itemData.variant === 'elevation';
        case 'borderRadius':
          return !itemData.square;
        default:
          return true;
      }
    },
    props: {
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        allowCustomColor: true,
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: paperVariant,
      },
      elevation: {
        label: 'Elevation',
        type: 'number',
        helpText: 'Shadow depth. It accepts values between 0 and 24 inclusive.',
        max: 24,
      },
      square: {
        label: 'Square',
        type: 'boolean',
        helpText: 'If checked, rounded corners are disabled.',
      },
      borderRadius: {
        label: 'Border Radius',
        type: 'number',
        max: 12,
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Spacing',
      },
      overflow: {
        label: 'Overflow',
        type: 'select',
        options: cssGridOverflow,
      },
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'sizeAware',
    icon: { iconName: 'square' },
    description: `**Overview**<br/>
The Paper component is a versatile container that mimics the physical properties of a sheet of paper. It is used to build the foundational "surfaces" of your user interface.
By assigning a background color and an "elevation" (drop shadow), the Paper component creates visual separation between the content and the underlying page. This depth hierarchy helps users distinguish between different sections of an application, such as a form area, a sidebar, or a highlighted metric.

**1. Visual Surface & Depth**<br/>
The defining characteristic of the Paper component is its ability to appear "lifted" off the screen.
* **Variant:** Determines the rendering style of the surface. (Elevation uses shadows; Outlined uses borders).
* **Elevation:** Controls the depth of the drop shadow (0–24). Low values (1-4) offer subtle separation; High values (8-24) create high contrast for floating elements. (Visible only when Variant is 'Elevation').
* **BG Color:** Sets the solid background color of the surface. You can use standard theme colors or custom hex codes to differentiate sections (e.g., a light grey paper on a white page).

**2. Shape & Definition**<br/>
These properties control the physical geometry of the container.
* **Square:** A boolean toggle that forces the component to have sharp, 90-degree corners. This is often used when the Paper component extends to the full edge of the screen (like a sidebar) where rounded corners would look unnatural.
* **Border Radius:** Fine-tunes the roundness of the corners. This allows you to match the "softness" of the container to your application's design language. (Note: This property is ignored if 'Square' is enabled).
* **Border:** Adds an explicit outline stroke around the container. This is useful for high-contrast interfaces or when the elevation is set to 0.

**3. Layout & Spacing**<br/>
The Paper component acts as a wrapper for your functional content.
* **Padding:** Defines the internal spacing between the edge of the paper and the content inside it. Generous padding is recommended to create a clean, professional look and prevent text from touching the borders.
* **Overflow:** Determines how the component handles content that exceeds its size constraints. (Hidden clips content; Scroll adds scrollbars; Auto adds scrollbars only when necessary).`,
    helpRenders: [
      {
        title: 'Paper with Elevation',
        description: 'Adjust the elevation',
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: ['elevation', 'variant'],
        item: {
          itemId: 'paper',
          props: { elevation: 3 },
          children: [],
          type: 'Paper',
          parent: 'root',
        },
      },
      {
        title: 'Paper with Background Color & Rounded Corners',
        description: 'Adjust the background color & border radius',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['backgroundColor', 'borderRadius'],
        item: {
          itemId: 'paper',
          props: { elevation: 3, backgroundColor: '#F5A623', borderRadius: 3 },
          children: [],
          type: 'Paper',
          parent: 'root',
        },
      },
    ],
  };
};

export default PaperManager;
